import { useState, useEffect } from "react";
import axios from "axios";
import { useToast } from "../admincomponents/AdminToast";
import { BASE_URL } from "../baseurl";

function getInitials(name) {
  return (name || "")
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export default function Customers() {
  const toast = useToast();
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("adminToken"); 

      const res = await axios.get(`${BASE_URL}/purchase/customers`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const mapped = res.data.customers.map((c) => ({
        id: c._id,
        name: c.username || "Unknown",
        email: c.email || "—",
        
       
        joined: c.createdAt ? new Date(c.createdAt).toISOString().slice(0, 10) : "—",
        orders: (c.orders || []).map((o) => ({
          id: o._id,
          date: o.createdAt ? new Date(o.createdAt).toISOString().slice(0, 10) : "—",
          total: o.totalPrice || 0,
        })),
      }));

      setCustomers(mapped);
    } catch (error) {
      console.error("Fetch customers error:", error);
      toast({
        variant: "error",
        title: "Couldn't load customers",
        description:
          error.response?.data?.message || "Something went wrong fetching customers.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openCustomerDetail = (customer) => {
    setSelectedCustomer(customer);
  };

  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch =
      customer.name.toLowerCase().includes(search.toLowerCase()) ||
      customer.email.toLowerCase().includes(search.toLowerCase());
    return matchesSearch;
  });

  const totalCustomers = customers.length;
  const totalSpent = customers.reduce(
    (sum, c) => sum + c.orders.reduce((s, o) => s + o.total, 0),
    0
  );

  const stats = [
    { label: "Total Customers", value: totalCustomers },
    { label: "Lifetime Revenue", value: `$${totalSpent.toFixed(2)}` },
  ];

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-gray-800 font-semibold text-2xl">Customers</h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage your store's customer base
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-8 max-w-md">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-white border border-gray-200 rounded-lg p-5">
            <p className="text-gray-500 text-xs">{stat.label}</p>
            <p className="text-gray-800 font-semibold text-xl mt-1">{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5" />
            <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-white border border-gray-300 rounded-md pl-9 pr-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-500"
          />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left">
                <th className="px-6 py-4 text-gray-500 font-medium">Customer</th>
                <th className="px-6 py-4 text-gray-500 font-medium">Orders</th>
                <th className="px-6 py-4 text-gray-500 font-medium">Total Spent</th>
                <th className="px-6 py-4 text-gray-500 font-medium">Joined</th>
                <th className="px-6 py-4 text-gray-500 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-gray-400">
                    Loading customers...
                  </td>
                </tr>
              )}

              {!isLoading &&
                filteredCustomers.map((customer) => {
                  const spent = customer.orders.reduce((s, o) => s + o.total, 0);
                  return (
                    <tr
                      key={customer.id}
                      className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-xs">
                            {getInitials(customer.name)}
                          </div>
                          <div>
                            <p className="text-gray-800 font-medium">{customer.name}</p>
                            <p className="text-gray-400 text-xs">{customer.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600">{customer.orders.length}</td>
                      <td className="px-6 py-4 text-gray-800 font-medium">
                        ${spent.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-gray-600">{customer.joined}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end">
                          <button
                            onClick={() => openCustomerDetail(customer)}
                            className="text-teal-600 hover:text-teal-700 font-medium text-sm"
                          >
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}

              {!isLoading && filteredCustomers.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-gray-400">
                    No customers match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedCustomer && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => setSelectedCustomer(null)}
          />

          <div className="fixed top-0 right-0 h-full w-full max-w-[480px] bg-white z-50 flex flex-col shadow-xl">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-xs">
                  {getInitials(selectedCustomer.name)}
                </div>
                <div>
                  <h2 className="text-gray-800 font-semibold text-lg">
                    {selectedCustomer.name}
                  </h2>
                  <p className="text-gray-400 text-xs mt-0.5">
                    Customer since {selectedCustomer.joined}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedCustomer(null)}
                className="text-gray-400 hover:text-gray-700"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="mb-6">
                <h3 className="text-gray-700 font-semibold text-sm mb-3">Contact Info</h3>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex flex-col gap-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Email</span>
                    <span className="text-gray-800">{selectedCustomer.email}</span>
                  </div>
                
                </div>
              </div>

       

              <div>
                <h3 className="text-gray-700 font-semibold text-sm mb-3">
                  Order History ({selectedCustomer.orders.length})
                </h3>
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {selectedCustomer.orders.map((order) => (
                    <div key={order.id} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-gray-800 text-sm font-medium">{order.id}</p>
                        <p className="text-gray-400 text-xs">{order.date}</p>
                      </div>
                      <p className="text-gray-800 text-sm font-medium">
                        ${order.total.toFixed(2)}
                      </p>
                    </div>
                  ))}
                  {selectedCustomer.orders.length === 0 && (
                    <p className="px-4 py-6 text-center text-gray-400 text-sm">
                      No orders yet.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 px-6 py-5">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 font-medium text-sm">Lifetime Value</span>
                <span className="text-gray-800 font-semibold text-lg">
                  $
                  {selectedCustomer.orders
                    .reduce((s, o) => s + o.total, 0)
                    .toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}